import React, { useState, useEffect } from 'react';
import ReactDatePicker from 'react-datepicker';
import { createPortal } from 'react-dom';
import 'react-datepicker/dist/react-datepicker.css';

interface DatePickerProps {
    selectedDate?: Date | null;
    onChange?: (date: Date | null) => void;
    dateFormat?: string;
    placeholder?: string;
    disabled?: boolean;
    minDate?: Date;
    maxDate?: Date;
    showTimeSelect?: boolean;
    timeFormat?: string;
    timeIntervals?: number;
    className?: string;
    isClearable?: boolean;
    showYearDropdown?: boolean;
    showMonthDropdown?: boolean;
    dropdownMode?: 'scroll' | 'select';
    size?: 'sm' | 'md' | 'lg';
}

const DatePicker = ({
    selectedDate = null,
    onChange,
    dateFormat = 'yyyy-MM-dd',
    placeholder = '날짜를 선택해주세요',
    disabled = false,
    minDate,
    maxDate,
    showTimeSelect = false,
    timeFormat = 'HH:mm',
    timeIntervals = 15,
    className = '',
    isClearable = true,
    showYearDropdown = true,
    showMonthDropdown = true,
    dropdownMode = 'scroll',
    size = 'md',
}: DatePickerProps) => {
    const [internalSelectedDate, setInternalSelectedDate] =
        useState<Date | null>(selectedDate);
    const [portalElement, setPortalElement] = useState<HTMLElement | null>(
        null,
    );

    useEffect(() => {
        setInternalSelectedDate(selectedDate);
    }, [selectedDate]);

    // Portal 엘리먼트 생성 및 정리
    useEffect(() => {
        const portal = document.getElementById('datepicker-portal');
        if (portal) {
            setPortalElement(portal);
        } else {
            const newPortal = document.createElement('div');
            newPortal.id = 'datepicker-portal';
            newPortal.style.position = 'fixed';
            newPortal.style.top = '0';
            newPortal.style.left = '0';
            newPortal.style.zIndex = '9999';
            newPortal.style.pointerEvents = 'none';
            document.body.appendChild(newPortal);
            setPortalElement(newPortal);
        }

        return () => {
            // 필요하면 정리, 보통은 전체 앱 생명주기 동안 유지
        };
    }, []);

    const handleDateChange = (date: Date | null) => {
        setInternalSelectedDate(date);
        if (onChange) {
            onChange(date);
        }
    };

    const handleReset = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) {
            setInternalSelectedDate(null);
            if (onChange) {
                onChange(null);
            }
        }
    };

    const finalDateFormat = showTimeSelect
        ? `${dateFormat} ${timeFormat}`
        : dateFormat;

    // 크기별 클래스명 결정
    const sizeClass = {
        sm: 'input-group-sm',
        md: '',
        lg: 'input-group-lg',
    }[size];

    const inputSizeClass = {
        sm: 'form-control-sm',
        md: '',
        lg: 'form-control-lg',
    }[size];

    const iconSizeClass = {
        sm: 'text-sm',
        md: '',
        lg: 'text-lg',
    }[size];

    return (
        <>
            <div className={`datepicker-inputwraper input-group ${sizeClass}`}>
                <span
                    className={`input-group-text text-muted ${iconSizeClass}`}
                >
                    <i className="ri-calendar-line"></i>
                </span>
                <div className="react-datepicker-wrapper flex-grow-1">
                    <ReactDatePicker
                        locale="ko"
                        dateFormatCalendar="yyyy년 MMMM" // 이 줄 추가!
                        selected={internalSelectedDate}
                        onChange={handleDateChange}
                        dateFormat={finalDateFormat}
                        placeholderText={placeholder}
                        disabled={disabled}
                        minDate={minDate}
                        maxDate={maxDate}
                        showTimeSelect={showTimeSelect}
                        timeFormat={timeFormat}
                        timeIntervals={timeIntervals}
                        showYearDropdown={showYearDropdown}
                        showMonthDropdown={showMonthDropdown}
                        dropdownMode={dropdownMode}
                        scrollableYearDropdown
                        yearDropdownItemNumber={15}
                        scrollableMonthYearDropdown
                        className={`form-control ${inputSizeClass} ${className}`}
                        wrapperClassName="w-100"
                        calendarClassName="shadow-lg"
                        popperClassName="react-datepicker-popper"
                        popperPlacement="bottom-start"
                        isClearable={false}
                        autoComplete="off"
                        showPopperArrow={false}
                        adjustDateOnChange={false}
                        preventOpenOnFocus={false}
                        shouldCloseOnSelect={true}
                        popperContainer={({ children }) =>
                            portalElement
                                ? createPortal(children, portalElement)
                                : null
                        }
                    />
                </div>
                {isClearable && (
                    <span
                        className={`input-group-text text-muted cursor-pointer ${iconSizeClass} ${
                            disabled ? 'opacity-50' : 'hover:bg-gray-100'
                        }`}
                        onClick={handleReset}
                        style={{ pointerEvents: disabled ? 'none' : 'auto' }}
                        title="초기화"
                    >
                        <i className="ri-close-line"></i>
                    </span>
                )}
            </div>
        </>
    );
};

export default DatePicker;
